import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { LayoutChangeEvent, StyleProp, ViewStyle } from "react-native";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
    Easing,
    ReduceMotion,
} from "react-native-reanimated";
import Gap from "./Gap";

type CollapsibleViewProps = {
    isOpen: boolean;
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

export const COLLAPSIBLE_VIEW_ANIMATION_DURATION = 450;

const ANIMATION_CONFIG = {
    duration: COLLAPSIBLE_VIEW_ANIMATION_DURATION,
    easing: Easing.out(Easing.cubic),
    reduceMotion: ReduceMotion.System,
};

const CollapsibleView = ({ isOpen, children, style }: CollapsibleViewProps) => {
    const contentHeight = useSharedValue(0);
    const progress = useSharedValue(isOpen ? 1 : 0);
    const initialIsOpen = useSharedValue(isOpen);
    const isInitialized = useRef(false);
    const cachedChildrenRef = useRef<React.ReactNode>(isOpen ? children : null);

    // Read-only during render: derive what to display without mutating refs.
    // We deliberately read the cached children to keep them visible during the
    // close animation. The write happens in the layout effect below.
    // eslint-disable-next-line react-hooks/refs
    const displayedChildren = isOpen ? children : (cachedChildrenRef.current ?? children);

    // Update the cache after commit, never during render
    useLayoutEffect(() => {
        if (isOpen) {
            cachedChildrenRef.current = children;
        }
    }, [isOpen, children]);

    useEffect(() => {
        if (isInitialized.current) {
            progress.value = withTiming(isOpen ? 1 : 0, ANIMATION_CONFIG);
        }
    }, [isOpen, progress]);

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const measuredHeight = event.nativeEvent.layout.height;
            if (measuredHeight > 0) {
                contentHeight.value = measuredHeight;
                if (!isInitialized.current) {
                    isInitialized.current = true;
                    progress.value = isOpen ? 1 : 0;
                }
            }
        },
        [contentHeight, progress, isOpen],
    );

    const containerStyle = useAnimatedStyle(() => {
        if (contentHeight.value === 0) {
            return { overflow: "hidden" as const };
        }
        return {
            height: progress.value * contentHeight.value,
            opacity: progress.value,
            overflow: "hidden" as const,
        };
    });

    const innerStyle = useAnimatedStyle(() => ({
        position: contentHeight.value > 0 || !initialIsOpen.value ? "absolute" : "relative",
        width: "100%",
    }));

    return (
        <Animated.View style={[containerStyle, style]}>
            <Animated.View onLayout={onLayout} style={innerStyle}>
                <Gap gap="2/3" />
                {displayedChildren}
            </Animated.View>
        </Animated.View>
    );
};

export default CollapsibleView;
